import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Search, Filter, Mail, Phone, Building2, FileDown, MessageCircle, Eye, Clock, CheckCircle2, AlertCircle, Paperclip } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_MAP = {
  nueva: { label: "Nueva", color: "bg-blue-100 text-blue-800 border-blue-200" },
  leida: { label: "Leida", color: "bg-yellow-100 text-yellow-800 border-yellow-200" },
  pendiente: { label: "Pendiente", color: "bg-orange-100 text-orange-800 border-orange-200" },
  gestionada: { label: "Gestionada", color: "bg-green-100 text-green-800 border-green-200" },
};

const REASONS = ["Información general", "Taller y postventa", "Recambios", "Chapa y pintura", "Vehículos usados y Km0", "Promociones", "Otra consulta"];

export default function ContactSubmissionsPage() {
  const { axiosAuth } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: "", center: "", reason: "", search: "" });
  const [page, setPage] = useState(0);
  const [detail, setDetail] = useState(null);
  const [centers, setCenters] = useState([]);
  const limit = 25;

  useEffect(() => { axiosAuth().get("/centers").then(r => setCenters(r.data)).catch(() => {}); }, [axiosAuth]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip: page * limit, limit };
      if (filters.status) params.status = filters.status;
      if (filters.center) params.center = filters.center;
      if (filters.reason) params.reason = filters.reason;
      if (filters.search) params.search = filters.search;
      const r = await axiosAuth().get("/contact/submissions", { params });
      setItems(r.data.items);
      setTotal(r.data.total);
    } catch { toast.error("Error al cargar consultas"); }
    setLoading(false);
  }, [axiosAuth, page, filters]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await axiosAuth().put(`/contact/submissions/${id}/status`, { status });
      toast.success("Estado actualizado");
      load();
      if (detail?.id === id) setDetail(p => ({ ...p, status }));
    } catch { toast.error("Error al actualizar estado"); }
  };

  const exportCsv = async () => {
    try {
      const r = await axiosAuth().get("/contact/submissions/export", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "consultas_contacto.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Exportacion descargada");
    } catch { toast.error("Error al exportar"); }
  };

  const fmt = (d) => d ? new Date(d).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-";

  return (
    <div className="space-y-6" data-testid="contact-submissions-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2"><Mail className="w-5 h-5 text-red-600" /> Consultas de Contacto</h1>
          <p className="text-sm text-slate-500">{total} consultas recibidas</p>
        </div>
        <Button variant="outline" size="sm" onClick={exportCsv} className="rounded-none text-xs uppercase tracking-wider" data-testid="export-contacts-btn"><FileDown className="w-3.5 h-3.5 mr-1.5" /> Exportar CSV</Button>
      </div>

      {/* Filters */}
      <Card className="border-slate-200 rounded-sm">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input placeholder="Buscar por nombre, email, telefono..." value={filters.search} onChange={e => { setFilters(p => ({...p, search: e.target.value})); setPage(0); }} className="pl-10 rounded-none h-9 text-sm" data-testid="search-contacts" />
              </div>
            </div>
            <Select value={filters.status || "all"} onValueChange={v => { setFilters(p => ({...p, status: v === "all" ? "" : v})); setPage(0); }}>
              <SelectTrigger className="w-[140px] rounded-none h-9 text-xs" data-testid="filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                {Object.entries(STATUS_MAP).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.reason || "all"} onValueChange={v => { setFilters(p => ({...p, reason: v === "all" ? "" : v})); setPage(0); }}>
              <SelectTrigger className="w-[160px] rounded-none h-9 text-xs" data-testid="filter-reason"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los motivos</SelectItem>
                {REASONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.center || "all"} onValueChange={v => { setFilters(p => ({...p, center: v === "all" ? "" : v})); setPage(0); }}>
              <SelectTrigger className="w-[140px] rounded-none h-9 text-xs" data-testid="filter-center"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los centros</SelectItem>
                {centers.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                <SelectItem value="Indiferente">Indiferente</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-slate-200 rounded-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-[10px] uppercase tracking-wider font-semibold">Fecha</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider font-semibold">Nombre</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider font-semibold">Contacto</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider font-semibold">Centro</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider font-semibold">Motivo</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider font-semibold">Estado</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider font-semibold w-[100px]">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center py-12 text-sm text-slate-400">Cargando...</TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-12 text-sm text-slate-400">No hay consultas</TableCell></TableRow>
              ) : items.map(it => {
                const st = STATUS_MAP[it.status] || STATUS_MAP.nueva;
                return (
                  <TableRow key={it.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => { setDetail(it); if (it.status === "nueva") updateStatus(it.id, "leida"); }} data-testid={`contact-row-${it.id}`}>
                    <TableCell className="text-xs text-slate-600">{fmt(it.created_at)}</TableCell>
                    <TableCell className="text-sm font-medium text-slate-900">{it.name}</TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-0.5">
                        <span className="text-xs text-slate-600 flex items-center gap-1"><Mail className="w-3 h-3" />{it.email}</span>
                        {it.phone && <span className="text-xs text-slate-500 flex items-center gap-1"><Phone className="w-3 h-3" />{it.phone}</span>}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-slate-600">{it.center || "-"}</TableCell>
                    <TableCell className="text-xs text-slate-600">{it.reason || "-"}</TableCell>
                    <TableCell><Badge variant="outline" className={`text-[10px] rounded-sm ${st.color}`}>{st.label}</Badge></TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" className="h-7 px-2"><Eye className="w-3.5 h-3.5" /></Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {total > limit && (
        <div className="flex justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)} className="rounded-none text-xs">Anterior</Button>
          <span className="text-xs text-slate-500 self-center">Pag. {page + 1} de {Math.ceil(total / limit)}</span>
          <Button variant="outline" size="sm" disabled={(page + 1) * limit >= total} onClick={() => setPage(p => p + 1)} className="rounded-none text-xs">Siguiente</Button>
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="text-lg font-bold">Consulta de {detail?.name}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-slate-500 text-xs block">Nombre</span><span className="font-medium">{detail.name}</span></div>
                <div><span className="text-slate-500 text-xs block">Fecha</span><span>{fmt(detail.created_at)}</span></div>
                <div><span className="text-slate-500 text-xs block">Email</span><a href={`mailto:${detail.email}`} className="text-red-600">{detail.email}</a></div>
                <div><span className="text-slate-500 text-xs block">Telefono</span>{detail.phone ? <a href={`tel:${detail.phone}`} className="text-red-600">{detail.phone}</a> : "-"}</div>
                <div><span className="text-slate-500 text-xs block">Centro</span><span>{detail.center || "-"}</span></div>
                <div><span className="text-slate-500 text-xs block">Motivo</span><span>{detail.reason || "-"}</span></div>
              </div>
              <div>
                <span className="text-slate-500 text-xs block mb-1">Mensaje</span>
                <p className="text-sm text-slate-700 bg-slate-50 p-3 rounded whitespace-pre-wrap">{detail.message || "(sin mensaje)"}</p>
              </div>
              {detail.attachment_filename && (
                <div className="flex items-center gap-2 text-sm"><Paperclip className="w-4 h-4 text-slate-400" /><span>{detail.attachment_filename}</span></div>
              )}
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">Estado:</span>
                <Select value={detail.status} onValueChange={v => updateStatus(detail.id, v)}>
                  <SelectTrigger className="w-[150px] rounded-none h-8 text-xs" data-testid="detail-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(STATUS_MAP).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
